import { useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from "react";
import type { Ref } from "react";
import { Button, Typography } from "antd";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import PdfViewer from "./PdfViewer";
import DocxPreview from "./DocxPreview";
import { PreviewSkeleton } from "./PreviewSkeleton";
import { PreviewErrorState } from "./PreviewErrorState";
import { useDocumentPreview } from "../../hooks/useDocumentPreview";
import { AUTH_TOKEN_KEY } from "../../constants/auth";
import type { Document, DocumentFormat } from "../../types";
import {
  clauseMatches,
  normalizeLocateText,
  splitRefParts,
  type LocateResult,
  type LocateTarget,
  type ViewerHandle,
} from "../../types/viewerLocate";
import { revealLocated } from "../../utils/locateHighlight";
import icon from "../../styles/icon.module.css";
import styles from "../../styles/workbench.module.css";

const { Text } = Typography;

// Formats routed through the preview endpoint (which returns a binary blob;
// Office formats pptx/xlsx are server-side converted to PDF via LibreOffice,
// see useDocumentPreview). docx and doc are intentionally absent — both are
// rendered in-browser via docx-preview: docx from its original bytes, doc from
// a server-side .doc→.docx conversion (/preview/docx). Both bypass the
// LO→PDF→pdfjs pipeline because pdfjs cannot render LO's subset CJK fonts.
const PREVIEWABLE_FORMATS = new Set<DocumentFormat>([
  "pdf",
  "pptx",
  "xlsx",
  "image",
]);

function isPreviewable(format: DocumentFormat | undefined): boolean {
  return !!format && PREVIEWABLE_FORMATS.has(format);
}

export interface DocumentPreviewProps {
  document: Document | null;
  isLoading?: boolean;
  /** Optional download handler; when provided, fallback renders a download button. */
  onDownload?: () => void;
  /** Forwarded to the inner PdfViewer / DocxPreview / TxtPreview so the parent
   * workbench can ask the active viewer to scroll to a finding's source. */
  viewerRef?: Ref<ViewerHandle>;
}

export default function DocumentPreview({
  document: doc,
  isLoading,
  onDownload,
  viewerRef,
}: DocumentPreviewProps) {
  // Only fetch the preview blob when the format is supported; pass null
  // otherwise so the hook stays idle and does not fire a doomed request.
  const previewDocumentId =
    doc && isPreviewable(doc.format) ? doc.id : null;
  const { state, retry } = useDocumentPreview(previewDocumentId);

  const content = useMemo(() => {
    if (isLoading) {
      return <PreviewSkeleton kind="pdf-loading" />;
    }

    if (!doc) {
      return (
        <div className={styles.detailCenter}>
          <div className={styles.previewContainer}>
            <div className={styles.previewIconWrap}>
              <FileOutlined className={icon.xl} />
            </div>
            <div className={styles.previewTitle}>选择文档开始预览</div>
            <div className={styles.previewDesc}>
              从左侧文档列表选择一个文件，此处将展示文档内容
            </div>
          </div>
        </div>
      );
    }

    // Plain text: fetch the body directly and render in a <pre>.
    if (doc.format === "txt") {
      return <TxtPreview documentId={doc.id} ref={viewerRef} />;
    }

    // docx: bypass the LO→PDF→pdfjs pipeline entirely — render original
    // bytes in-browser via docx-preview. pdfjs v5 stopAtErrors soft-fails
    // LO's NotoSansCJK subset fonts to blank pages; docx-preview uses the
    // browser's native font stack and avoids the issue.
    if (doc.format === "docx") {
      return (
        <div className={styles.detailCenter}>
          <DocxPreview documentId={doc.id} ref={viewerRef} />
        </div>
      );
    }

    // doc (legacy Word OLE2): can't be parsed by docx-preview directly, so the
    // backend converts .doc→.docx via LibreOffice (/preview/docx) and we render
    // the result with the same docx-preview path. Same CJK rationale as docx —
    // avoid the LO→PDF→pdfjs pipeline whose subset CJK fonts render blank.
    if (doc.format === "doc") {
      return (
        <div className={styles.detailCenter}>
          <DocxPreview
            documentId={doc.id}
            fetchUrl={`/api/v1/jerry/documents/${doc.id}/preview/docx`}
            ref={viewerRef}
          />
        </div>
      );
    }

    // Unsupported format: render fallback immediately without waiting on the
    // (idle) preview hook state.
    if (!isPreviewable(doc.format)) {
      return (
        <div className={styles.detailCenter}>
          <div className={styles.previewContainer}>
            <div className={styles.previewIconWrap}>
              <FileOutlined className={icon.xl} />
            </div>
            <div className={styles.previewTitle}>{doc.title}</div>
            <Text type="secondary">该格式暂不支持预览，请下载查看</Text>
            {onDownload && (
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={onDownload}
                >
                  下载文件
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    }

    const isOffice = doc.format === "pptx" || doc.format === "xlsx";

    if (state.status === "loading" || state.status === "idle") {
      return (
        <PreviewSkeleton kind={isOffice ? "office-converting" : "pdf-loading"} />
      );
    }
    if (state.status === "converting") {
      return <PreviewSkeleton kind="office-converting" />;
    }
    if (state.status === "error") {
      return (
        <PreviewErrorState
          errorCode={state.errorCode}
          errorMessage={state.errorMessage}
          onRetry={retry}
        />
      );
    }

    // state.status === "ready"
    if (state.url) {
      if (doc.format === "image") {
        return (
          <div className={styles.detailCenter}>
            <img
              src={state.url}
              alt={doc.title || doc.id}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          </div>
        );
      }
      if (doc.format === "pdf") {
        return (
          <div className={styles.detailCenter}>
            <PdfViewer url={state.url} ref={viewerRef} />
          </div>
        );
      }
      // pptx / xlsx fall through here — keep them on LO→PDF→pdfjs (their
      // OOXML is too complex for docx-preview; pdfjs soft-fail is acceptable
      // since CJK rendering issues are less prevalent than for docx).
      return (
        <div className={styles.detailCenter}>
          <PdfViewer url={state.url} ref={viewerRef} />
        </div>
      );
    }

    // Defensive: ready but no url — treat as fallback.
    return <PreviewSkeleton kind="pdf-loading" />;
  }, [isLoading, doc, state, onDownload, retry, viewerRef]);

  return (
    <div data-testid="document-preview" className={styles.detailPreviewRoot}>
      {content}
    </div>
  );
}

/** Map an HTTP status to the shared PreviewErrorState error code. */
function statusToErrorCode(status: number): string {
  if (status === 401) return "UNAUTHORIZED";
  if (status === 403) return "FORBIDDEN";
  if (status === 404) return "NOT_FOUND";
  return "UNKNOWN";
}

type TxtState =
  | { status: "loading" }
  | { status: "ready"; text: string }
  | { status: "error"; code: string };

const TxtPreview = forwardRef<
  ViewerHandle,
  { documentId: string }
>(function TxtPreview({ documentId }, ref) {
  const [state, setState] = useState<TxtState>({ status: "loading" });
  // Bump to force a refetch from the error state's retry button.
  const [attempt, setAttempt] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // One ref per rendered line so locate() can scroll + highlight the exact line.
  const lineRefs = useRef<Array<HTMLDivElement | null>>([]);

  useImperativeHandle(
    ref,
    (): ViewerHandle => ({
      async locate(target: LocateTarget): Promise<LocateResult> {
        if (state.status !== "ready") return "not-ready";

        const lines = state.text.split("\n");
        const clause = target.clauseText?.trim();

        // 1. clauseText — the verbatim line the AI quoted.
        if (clause) {
          for (let i = 0; i < lines.length; i += 1) {
            if (clauseMatches(lines[i], clause)) {
              revealLocated(lineRefs.current[i] ?? null);
              return "ok";
            }
          }
        }
        // 2. ref fallback — the first line mentioning any candidate heading.
        for (const part of splitRefParts(target.ref)) {
          for (let i = 0; i < lines.length; i += 1) {
            if (normalizeLocateText(lines[i]).includes(part)) {
              revealLocated(lineRefs.current[i] ?? null);
              return "ok";
            }
          }
        }
        return "not-found";
      },
    }),
    [state],
  );

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    const token =
      typeof localStorage !== "undefined"
        ? (localStorage.getItem(AUTH_TOKEN_KEY) ?? "")
        : "";
    fetch(`/api/v1/jerry/documents/${documentId}/preview`, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    })
      .then(async (r) => {
        if (!r.ok) {
          // Distinguish 401/403/404 so the user gets an accurate, actionable
          // message instead of a generic "加载失败".
          const code = statusToErrorCode(r.status);
          if (!cancelled) setState({ status: "error", code });
          return;
        }
        const body = await r.text();
        if (!cancelled) setState({ status: "ready", text: body });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", code: "UNKNOWN" });
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, attempt]);

  if (state.status === "error") {
    return (
      <PreviewErrorState
        errorCode={state.code}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    );
  }

  const lines = state.status === "ready" ? state.text.split("\n") : [];

  return (
    <div
      ref={scrollRef}
      style={{
        padding: 16,
        maxHeight: "70vh",
        overflow: "auto",
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
        fontSize: 13,
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
      }}
    >
      {state.status === "ready" ? (
        lines.map((line, i) => (
          <div
            key={i}
            ref={(el) => {
              lineRefs.current[i] = el;
            }}
          >
            {line || " "}
          </div>
        ))
      ) : (
        <span>加载中…</span>
      )}
    </div>
  );
});
